import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { getPartnershipPackageByToken } from "@/lib/partnership-leads.functions";
import heroImg from "@/assets/cc_main_hero.png";

export const Route = createFileRoute("/partnership-package/$token")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "ClutchCube Partnership Package" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: PackageViewer,
});

const packageSections: { title: string; body: string; items?: string[] }[] = [
  {
    title: "Executive Summary",
    body: "ClutchCube is the world's first immersive sports simulation chamber — a new category of experiential sports entertainment designed for the Experience Economy. This package outlines strategic opportunities for partners, sponsors, and franchise developers.",
  },
  {
    title: "Sponsorship Categories",
    body: "Sponsors become part of unforgettable moments rather than traditional advertisements.",
    items: [
      "Presenting Sponsor",
      "Official Equipment, Apparel, Technology & Beverage Partners",
      "Automotive, Financial, Airline & Media Partners",
      "Moment-Level & Challenge Sponsorships",
    ],
  },
  {
    title: "Activation Concepts",
    body: "Custom brand integrations across venue, digital, licensing, and content channels — designed to deliver measurable engagement, hospitality, and content output.",
  },
  {
    title: "Venue Integrations",
    body: "Flagship ClutchCube venues integrate into entertainment districts, stadium districts, resorts, casinos, and mixed-use developments. Multiple footprint formats support diverse partner needs.",
  },
  {
    title: "Franchise & Development",
    body: "Regional developers, master franchise groups, and international expansion partners can participate in ClutchCube's long-term rollout across premier entertainment markets.",
  },
  {
    title: "Future Expansion Plans",
    body: "Additional package materials — brand guidelines, venue development guide, technology overview, case studies, and franchise information — will be delivered to qualified partners as they become available.",
  },
];

function PackageViewer() {
  const { token } = Route.useParams();
  const fetchPkg = useServerFn(getPartnershipPackageByToken);
  const [state, setState] = useState<
    | { status: "loading" }
    | { status: "ok"; contactName: string; company: string; category: string }
    | { status: "invalid" }
    | { status: "error"; message: string }
  >({ status: "loading" });

  useEffect(() => {
    let alive = true;
    fetchPkg({ data: { token } })
      .then((res) => {
        if (!alive) return;
        if (res.ok) {
          setState({
            status: "ok",
            contactName: res.contactName,
            company: res.company,
            category: res.category,
          });
        } else {
          setState({ status: "invalid" });
        }
      })
      .catch((e) => alive && setState({ status: "error", message: (e as Error).message }));
    return () => {
      alive = false;
    };
  }, [token, fetchPkg]);

  if (state.status === "loading") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">
        Loading your partnership package…
      </div>
    );
  }

  if (state.status === "invalid" || state.status === "error") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="max-w-md text-center bg-card border border-border rounded-sm p-10">
          <p className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Invalid Link</p>
          <h1 className="font-display text-3xl mb-3">This package link isn&rsquo;t valid</h1>
          <p className="text-sm text-muted-foreground">
            The link may have expired or been mistyped. Please request a fresh copy from the
            ClutchCube partnerships team.
          </p>
          <a
            href="/partnerships"
            className="mt-6 inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground font-semibold uppercase tracking-widest text-xs rounded-sm"
          >
            Request Access
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="flex items-center justify-between px-6 py-4 border-b border-border bg-background/80 backdrop-blur">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-primary">
            ClutchCube · Confidential Partnership Package
          </p>
          <p className="text-sm text-muted-foreground">
            Prepared for {state.contactName} · {state.company}
          </p>
        </div>
        <a
          href="/partnerships#partner-form"
          className="inline-flex items-center px-5 py-2.5 bg-primary text-primary-foreground font-semibold uppercase tracking-widest text-[11px] rounded-sm shadow-glow hover:translate-y-[-1px] transition"
        >
          Schedule a Call
        </a>
      </header>

      <section className="relative py-24 overflow-hidden border-b border-border">
        <img
          src={heroImg}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/85 to-background" />
        <div className="absolute inset-0 led-grid opacity-25" />
        <div className="relative mx-auto max-w-4xl px-6">
          <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">
            {state.category} Track
          </p>
          <h1 className="font-display text-5xl sm:text-7xl leading-[0.98]">
            Partnership &amp; Sponsorship Package
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl leading-relaxed">
            A confidential overview of ClutchCube&rsquo;s partnership opportunities, prepared
            for {state.company}.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-3xl px-6 space-y-10">
          {packageSections.map((s, i) => (
            <div
              key={s.title}
              className="border border-border/70 bg-card/60 rounded-sm p-8"
            >
              <p className="text-[11px] uppercase tracking-[0.3em] text-primary">
                Section {String(i + 1).padStart(2, "0")}
              </p>
              <h2 className="mt-3 font-display text-2xl sm:text-3xl text-foreground">
                {s.title}
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">{s.body}</p>
              {s.items && (
                <ul className="mt-4 space-y-2 text-muted-foreground">
                  {s.items.map((it) => (
                    <li key={it} className="flex gap-3">
                      <span className="text-primary">&rsaquo;</span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          <div className="border border-primary/40 bg-card/70 rounded-sm p-8 text-center">
            <p className="text-[11px] uppercase tracking-[0.3em] text-primary">Next Step</p>
            <h2 className="mt-3 font-display text-2xl text-foreground">
              Let&rsquo;s schedule a strategy conversation.
            </h2>
            <p className="mt-3 text-muted-foreground">
              Reply to your welcome email or use the button below and our team will follow up
              within one business day.
            </p>
            <a
              href="/partnerships#partner-form"
              className="mt-6 inline-flex items-center px-8 py-4 bg-primary text-primary-foreground font-semibold uppercase tracking-widest text-xs rounded-sm shadow-glow hover:translate-y-[-1px] transition"
            >
              Schedule a Conversation
            </a>
          </div>

          <p className="text-center text-[11px] uppercase tracking-[0.3em] text-muted-foreground pt-6">
            Confidential · Please do not forward
          </p>
        </div>
      </section>
    </div>
  );
}
